interface YouTubeModalOptions {
  autoplay?: boolean;
  closeOnEscape?: boolean;
  closeOnBackdropClick?: boolean;
}

class YouTubeModal {
  private modal: HTMLElement | null = null;
  private isOpen: boolean = false;
  private options: YouTubeModalOptions;
  private element: HTMLElement;
  private modalId: string;
  private player: any = null;
  private playerReady: boolean = false;
  private originalScrollY: number = 0;
  
  // 静的プロパティでAPI読み込み状態を管理
  private static isAPILoading: boolean = false;
  private static isAPILoaded: boolean = false;
  private static pendingInstances: YouTubeModal[] = [];
  private static escapeListenerAdded: boolean = false;
  private static activeModals: YouTubeModal[] = [];

  constructor(element: HTMLElement, options: YouTubeModalOptions = {}) {
    this.element = element;
    this.modalId = this.generateUniqueId();
    this.options = {
      autoplay: true,
      closeOnEscape: true,
      closeOnBackdropClick: true,
      ...options
    };
    this.init();
  }

  /**
   * YouTube IFrame Player APIを読み込み（静的メソッド）
   */
  private static loadYouTubeAPI(): void {
    // 既に読み込み済みまたは読み込み中の場合は何もしない
    if (YouTubeModal.isAPILoaded || YouTubeModal.isAPILoading) {
      return;
    }

    // APIが既に存在する場合
    if (window.YT && window.YT.Player) {
      YouTubeModal.isAPILoaded = true;
      YouTubeModal.onAPIReady();
      return;
    }

    // スクリプトタグが既に存在するかチェック
    const existingScript = document.querySelector('script[src*="youtube.com/iframe_api"]');
    if (existingScript) {
      YouTubeModal.isAPILoading = true;
      YouTubeModal.waitForAPI();
      return;
    }

    // APIスクリプトを読み込み
    YouTubeModal.isAPILoading = true;
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    // グローバルコールバックを設定
    const originalReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (originalReady) originalReady();
      YouTubeModal.onAPIReady();
    };
  }

  /**
   * APIの準備完了を待つ
   */
  private static waitForAPI(): void {
    const checkAPI = () => {
      if (window.YT && window.YT.Player) {
        YouTubeModal.onAPIReady();
      } else {
        setTimeout(checkAPI, 100);
      }
    };
    checkAPI();
  }

  /**
   * API準備完了時の処理
   */
  private static onAPIReady(): void {
    YouTubeModal.isAPILoaded = true;
    YouTubeModal.isAPILoading = false;
    
    // 待機中のインスタンスを初期化
    YouTubeModal.pendingInstances.forEach(instance => {
      instance.initializePlayer();
    });
    YouTubeModal.pendingInstances = [];
  }

  /**
   * インスタンス用のAPI読み込み処理
   */
  private loadYouTubeAPI(): void {
    // 静的メソッドでAPI読み込みを管理
    YouTubeModal.loadYouTubeAPI();
    
    if (YouTubeModal.isAPILoaded) {
      // 既に読み込み済みの場合はすぐに初期化
      setTimeout(() => this.initializePlayer(), 100);
    } else {
      // 読み込み待ちリストに追加
      YouTubeModal.pendingInstances.push(this);
    }
  }

  /**
   * YouTube Playerを初期化（プレイヤーのみ作成、動画は後でロード）
   */
  private initializePlayer(): void {
    if (!this.modal || this.player) return;
    
    const playerId = `player_${this.modalId}`;
    const videoContainer = this.modal.querySelector('.ms-c-youtube-modal-video-container');
    
    if (!videoContainer) {
      console.error('Video container not found');
      return;
    }
    
    // プレイヤー用のdivを作成
    const playerDiv = document.createElement('div');
    playerDiv.id = playerId;
    playerDiv.style.width = '100%';
    playerDiv.style.height = '100%';
    videoContainer.appendChild(playerDiv);

    try {
      // シンプルなパラメータで初期化
      this.player = new window.YT.Player(playerId, {
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 0, // 初期化時は自動再生しない
          rel: 0, // 関連動画を表示しない
          modestbranding: 1, // YouTubeロゴを小さく
          origin: window.location.origin // セキュリティのため必須
        },
        events: {
          onReady: () => {
            console.log('Player ready');
            this.playerReady = true;
          },
          onError: (event: any) => {
            console.error('YouTube Player error:', event.data);
            // エラーコードの詳細
            const errorMessages: { [key: number]: string } = {
              2: 'リクエストに無効なパラメータ値が含まれています',
              5: 'HTML5プレーヤーに関するエラー',
              100: '動画が見つかりません（非公開または削除された可能性）',
              101: '動画の所有者が埋め込みを許可していません',
              150: '動画の所有者が埋め込みを許可していません'
            };
            console.error('エラー詳細:', errorMessages[event.data] || '不明なエラー');
          }
        }
      });
    } catch (error) {
      console.error('Failed to create YouTube player:', error);
    }
  }

  /**
   * ユニークなIDを生成
   */
  private generateUniqueId(): string {
    return `youtubeModal_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * 初期化処理
   */
  private init(): void {
    this.bindElementClickEvent();
  }

  /**
   * モーダルHTMLを作成
   */
  private createModal(): void {
    const existingModal = document.querySelector(`#${this.modalId}`);
    if (existingModal) {
      existingModal.remove();
    }

    const modalHTML = `
      <div class="ms-c-youtube-modal" id="${this.modalId}">
        <div class="ms-c-youtube-modal-content">
          <div class="ms-c-youtube-modal-close-wrapper">
            <button class="ms-c-youtube-modal-close" aria-label="閉じる">&times;</button>
          </div>
          <div class="ms-c-youtube-modal-video-container">
            <!-- プレイヤーはここに動的に追加される -->
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById(this.modalId);
  }

  /**
   * 要素のクリックイベントを設定
   */
  private bindElementClickEvent(): void {
    this.element.addEventListener('click', (e: Event) => {
      e.preventDefault();
      this.openModalFromElement();
    });
  }

  /**
   * イベントハンドラーを設定
   */
  private bindEvents(): void {
    if (!this.modal) return;

    // モーダル関連のイベント
    this.modal.addEventListener('click', (e: Event) => {
      if (!this.options.closeOnBackdropClick) return;

      const target = e.target as HTMLElement;
      const modalContent = this.modal?.querySelector('.ms-c-youtube-modal-content') as HTMLElement;

      if (modalContent && !modalContent.contains(target)) {
        this.closeModal();
      }
    });

    // 閉じるボタンのクリックイベント
    const closeBtn = this.modal.querySelector('.ms-c-youtube-modal-close') as HTMLButtonElement;
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.closeModal();
      });
    }

    // ESCキーでモーダルを閉じる（一度だけ設定）
    if (this.options.closeOnEscape && !YouTubeModal.escapeListenerAdded) {
      document.addEventListener('keydown', this.handleEscapeKey.bind(this));
      YouTubeModal.escapeListenerAdded = true;
    }
  }

  /**
   * 要素からYouTube動画IDを取得してモーダルを開く
   */
  private openModalFromElement(): void {
    // data-youtube属性から直接ビデオIDを取得
    let videoId = this.element.getAttribute('data-youtube');
    
    // data-youtube-embed属性からビデオIDを抽出
    if (!videoId) {
      const embedCode = this.element.getAttribute('data-youtube-embed');
      if (embedCode) {
        videoId = this.extractVideoIdFromEmbed(embedCode);
      }
    }
    
    if (!videoId) {
      console.error('YouTube video ID not found in data-youtube or data-youtube-embed attributes');
      return;
    }

    console.log('Video ID found:', videoId);

    // 初回のみモーダルDOMとAPIを準備
    if (!this.modal) {
      this.createModal();
      this.bindEvents();
      this.loadYouTubeAPI();
    }

    // プレイヤーが準備できるまで待ってからモーダルを開く
    this.waitForPlayerAndOpen(videoId);
  }

  /**
   * 埋め込みコードからビデオIDを抽出
   */
  private extractVideoIdFromEmbed(embedCode: string): string | null {
    const decodedEmbedCode = decodeURIComponent(embedCode.replace(/\+/g, ' '));
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = decodedEmbedCode.trim();
    const iframe = tempDiv.querySelector('iframe') as HTMLIFrameElement;
    
    if (iframe && iframe.src) {
      const match = iframe.src.match(/\/embed\/([a-zA-Z0-9_-]+)/);
      return match ? match[1] : null;
    }
    return null;
  }

  /**
   * モーダルを開く
   * @param videoId - YouTube動画ID
   */
  public openModal(videoId: string): void {
    if (this.isOpen || !this.modal) return;

    console.log('Opening modal with video ID:', videoId);

    // アクティブなモーダルリストに追加
    YouTubeModal.activeModals.push(this);

    // スクロールロックを適用
    this.lockScroll();

    // モーダルを表示
    this.modal.style.display = 'flex';
    
    setTimeout(() => {
      this.modal?.classList.add('is-show');
    }, 10);

    // プレイヤーが準備できていれば動画をロード
    if (this.player && this.playerReady) {
      this.loadVideo(videoId);
    }

    this.isOpen = true;
  }

  /**
   * 動画をロード
   */
  private loadVideo(videoId: string): void {
    if (!this.player || !this.playerReady) return;

    console.log('Loading video:', videoId);
    
    try {
      if (this.options.autoplay) {
        this.player.loadVideoById(videoId);
      } else {
        this.player.cueVideoById(videoId);
      }
    } catch (error) {
      console.error('Error loading video:', error);
    }
  }

  /**
   * 動画を手動再生
   */
  public playVideo(): void {
    if (this.player && this.playerReady && this.player.playVideo) {
      try {
        this.player.playVideo();
      } catch (error) {
        console.error('Error playing video:', error);
      }
    }
  }

  /**
   * 動画を一時停止
   */
  public pauseVideo(): void {
    if (this.player && this.playerReady && this.player.pauseVideo) {
      try {
        this.player.pauseVideo();
      } catch (error) {
        console.error('Error pausing video:', error);
      }
    }
  }

  /**
   * モーダルを閉じる
   */
  public closeModal(): void {
    if (!this.isOpen || !this.modal) return;

    console.log('Closing modal');

    // 動画を停止
    if (this.player && this.playerReady && this.player.stopVideo) {
      try {
        this.player.stopVideo();
      } catch (error) {
        console.error('Error stopping video:', error);
      }
    }

    // アクティブなモーダルリストから削除
    const index = YouTubeModal.activeModals.indexOf(this);
    if (index > -1) {
      YouTubeModal.activeModals.splice(index, 1);
    }

    this.modal.classList.remove('is-show');

    setTimeout(() => {
      if (!this.modal) return;
      
      this.modal.style.display = 'none';
      this.unlockScroll();
    }, 300);

    this.isOpen = false;
  }

  /**
   * プレイヤーの準備を待ってからモーダルを開く
   */
  private waitForPlayerAndOpen(videoId: string): void {
    let attempts = 0;
    const maxAttempts = 50; // 最大5秒待つ

    const checkPlayer = () => {
      attempts++;
      
      if (this.player && this.playerReady) {
        // プレイヤーが準備できたらモーダルを開く
        this.openModal(videoId);
      } else if (attempts < maxAttempts) {
        // まだ準備できていない場合は少し待って再確認
        setTimeout(checkPlayer, 100);
      } else {
        console.error('Player initialization timeout');
      }
    };
    
    checkPlayer();
  }

  /**
   * モーダルが開いているかどうかを取得
   */
  public getIsOpen(): boolean {
    return this.isOpen;
  }

  /**
   * モーダルを破棄
   */
  public destroy(): void {
    // ペンディングリストからこのインスタンスを削除
    const index = YouTubeModal.pendingInstances.indexOf(this);
    if (index > -1) {
      YouTubeModal.pendingInstances.splice(index, 1);
    }
    
    if (this.player && this.player.destroy) {
      try {
        this.player.destroy();
      } catch (error) {
        console.error('Error destroying player:', error);
      }
      this.player = null;
    }
    
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }
    this.isOpen = false;
    this.playerReady = false;
    this.unlockScroll();
  }

  /**
   * ESCキーハンドラー（静的メソッド）
   */
  private handleEscapeKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      // 最後に開かれたモーダルを閉じる
      const lastModal = YouTubeModal.activeModals[YouTubeModal.activeModals.length - 1];
      if (lastModal && lastModal.isOpen) {
        lastModal.closeModal();
      }
    }
  }

  /**
   * スクロールをロック
   */
  private lockScroll(): void {
    // 現在のスクロール位置を保存
    this.originalScrollY = window.scrollY;
    
    // スクロールバーの幅を計算
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // body要素にスタイルを適用
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.originalScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    
    // スクロールバーが存在する場合は右側にパディングを追加
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  /**
   * スクロールのロックを解除
   */
  private unlockScroll(): void {
    // 他にアクティブなモーダルがある場合はスクロールロックを維持
    if (YouTubeModal.activeModals.length > 0) {
      return;
    }

    // body要素のスタイルをリセット
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';
    
    // 元のスクロール位置に戻す
    window.scrollTo(0, this.originalScrollY);
  }
}

// グローバル変数の型定義
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

// 使用例
document.addEventListener('DOMContentLoaded', () => {
  const youTubeImgElements = document.querySelectorAll('.js-youtube-img');
  youTubeImgElements.forEach((element) => {
    new YouTubeModal(element as HTMLElement);
  });
});

export default YouTubeModal;